import { useState } from 'react'
import {
  MapPin,
  Phone,
  Mail,
  Plus,
  Pencil,
  Trash2,
  Star,
  X,
  Check,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { kontakteAPI } from '@/utils/api'
import type { KontaktDetail, KontaktAdresse, KontaktTelefon, KontaktEmail } from '@/utils/api'

interface KontaktdatenTabProps {
  kontakt: KontaktDetail
  onRefresh: () => void
}

// Address types
const ADDRESS_TYPES = [
  { value: 'privat', label: 'Privatadresse' },
  { value: 'geschaeft', label: 'Geschäftsadresse' },
  { value: 'rechnung', label: 'Rechnungsadresse' },
  { value: 'post', label: 'Postadresse' },
]

// Phone types
const PHONE_TYPES = [
  { value: 'mobil', label: 'Mobil' },
  { value: 'privat', label: 'Privat' },
  { value: 'geschaeft', label: 'Geschäftlich' },
  { value: 'fax', label: 'Fax' },
]

// Email types
const EMAIL_TYPES = [
  { value: 'privat', label: 'Privat' },
  { value: 'geschaeft', label: 'Geschäftlich' },
  { value: 'rechnung', label: 'Rechnung' },
]

export function KontaktdatenTab({ kontakt, onRefresh }: KontaktdatenTabProps) {
  return (
    <div className="space-y-6">
      {/* Addresses Section */}
      <AddressesSection kontakt={kontakt} onRefresh={onRefresh} />

      {/* Phones Section */}
      <PhonesSection kontakt={kontakt} onRefresh={onRefresh} />

      {/* Emails Section */}
      <EmailsSection kontakt={kontakt} onRefresh={onRefresh} />
    </div>
  )
}

// ============ ADDRESSES SECTION ============

function AddressesSection({ kontakt, onRefresh }: { kontakt: KontaktDetail; onRefresh: () => void }) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [formData, setFormData] = useState({
    typ: 'privat' as string,
    strasse: '',
    hausnummer: '',
    zusatz: '',
    plz: '',
    ort: '',
    land: 'Deutschland',
    is_primary: false,
  })

  const resetForm = () => {
    setFormData({
      typ: 'privat',
      strasse: '',
      hausnummer: '',
      zusatz: '',
      plz: '',
      ort: '',
      land: 'Deutschland',
      is_primary: false,
    })
  }

  const startEdit = (address: KontaktAdresse) => {
    setEditingId(address.id)
    setFormData({
      typ: address.typ,
      strasse: address.strasse,
      hausnummer: address.hausnummer || '',
      zusatz: address.zusatz || '',
      plz: address.plz,
      ort: address.ort,
      land: address.land,
      is_primary: address.is_primary,
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (editingId) {
        await kontakteAPI.updateAddress(kontakt.id, editingId, formData)
        toast.success('Adresse aktualisiert')
      } else {
        await kontakteAPI.createAddress(kontakt.id, formData)
        toast.success('Adresse hinzugefügt')
      }
      onRefresh()
      resetForm()
      setIsAdding(false)
      setEditingId(null)
    } catch (err) {
      console.error('Failed to save address:', err)
      toast.error('Adresse konnte nicht gespeichert werden')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (addressId: number) => {
    setDeletingId(addressId)
    try {
      await kontakteAPI.deleteAddress(kontakt.id, addressId)
      toast.success('Adresse gelöscht')
      onRefresh()
    } catch (err) {
      console.error('Failed to delete address:', err)
      toast.error('Adresse konnte nicht gelöscht werden')
    } finally {
      setDeletingId(null)
    }
  }

  const cancelEdit = () => {
    resetForm()
    setIsAdding(false)
    setEditingId(null)
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-slate-400" />
          Adressen
        </h2>
        {!isAdding && !editingId && (
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Hinzufügen
          </Button>
        )}
      </div>

      {/* Address List */}
      <div className="space-y-3">
        {kontakt.addresses.map((address) =>
          editingId === address.id ? (
            <AddressForm
              key={address.id}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={cancelEdit}
              isSaving={isSaving}
            />
          ) : (
            <div
              key={address.id}
              className="flex items-start justify-between p-3 rounded-lg bg-slate-50 group"
            >
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-200 text-slate-700">
                    {address.typ_display}
                  </span>
                  {address.is_primary && (
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-900">{address.full_address}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => startEdit(address)}
                  className="h-8 w-8 p-0"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(address.id)}
                  disabled={deletingId === address.id}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  {deletingId === address.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )
        )}

        {/* Add Form */}
        {isAdding && (
          <AddressForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={cancelEdit}
            isSaving={isSaving}
          />
        )}

        {/* Empty State */}
        {kontakt.addresses.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 text-center py-4">
            Keine Adressen vorhanden
          </p>
        )}
      </div>
    </div>
  )
}

function AddressForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isSaving,
}: {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  isSaving: boolean
}) {
  return (
    <div className="p-4 rounded-lg border border-slate-300 bg-slate-50 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Typ</Label>
          <Select
            value={formData.typ}
            onValueChange={(value) => setFormData({ ...formData, typ: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ADDRESS_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={formData.is_primary}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_primary: !!checked })
              }
            />
            <span className="text-sm text-slate-700">Primäre Adresse</span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-2">
          <Label>Straße</Label>
          <Input
            value={formData.strasse}
            onChange={(e) => setFormData({ ...formData, strasse: e.target.value })}
            placeholder="Musterstraße"
          />
        </div>
        <div className="space-y-2">
          <Label>Hausnummer</Label>
          <Input
            value={formData.hausnummer}
            onChange={(e) => setFormData({ ...formData, hausnummer: e.target.value })}
            placeholder="123"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Zusatz</Label>
        <Input
          value={formData.zusatz}
          onChange={(e) => setFormData({ ...formData, zusatz: e.target.value })}
          placeholder="c/o, Etage, etc."
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>PLZ</Label>
          <Input
            value={formData.plz}
            onChange={(e) => setFormData({ ...formData, plz: e.target.value })}
            placeholder="12345"
          />
        </div>
        <div className="space-y-2">
          <Label>Ort</Label>
          <Input
            value={formData.ort}
            onChange={(e) => setFormData({ ...formData, ort: e.target.value })}
            placeholder="Berlin"
          />
        </div>
        <div className="space-y-2">
          <Label>Land</Label>
          <Input
            value={formData.land}
            onChange={(e) => setFormData({ ...formData, land: e.target.value })}
            placeholder="Deutschland"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          <X className="h-4 w-4 mr-1" />
          Abbrechen
        </Button>
        <Button onClick={onSave} disabled={isSaving || !formData.strasse || !formData.plz || !formData.ort}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          Speichern
        </Button>
      </div>
    </div>
  )
}

// ============ PHONES SECTION ============

function PhonesSection({ kontakt, onRefresh }: { kontakt: KontaktDetail; onRefresh: () => void }) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [formData, setFormData] = useState({
    typ: 'mobil' as string,
    nummer: '',
    is_primary: false,
  })

  const resetForm = () => {
    setFormData({ typ: 'mobil', nummer: '', is_primary: false })
  }

  const startEdit = (phone: KontaktTelefon) => {
    setEditingId(phone.id)
    setFormData({
      typ: phone.typ,
      nummer: phone.nummer,
      is_primary: phone.is_primary,
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (editingId) {
        await kontakteAPI.updatePhone(kontakt.id, editingId, formData)
        toast.success('Telefonnummer aktualisiert')
      } else {
        await kontakteAPI.createPhone(kontakt.id, formData)
        toast.success('Telefonnummer hinzugefügt')
      }
      onRefresh()
      resetForm()
      setIsAdding(false)
      setEditingId(null)
    } catch (err) {
      console.error('Failed to save phone:', err)
      toast.error('Telefonnummer konnte nicht gespeichert werden')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (phoneId: number) => {
    setDeletingId(phoneId)
    try {
      await kontakteAPI.deletePhone(kontakt.id, phoneId)
      toast.success('Telefonnummer gelöscht')
      onRefresh()
    } catch (err) {
      console.error('Failed to delete phone:', err)
      toast.error('Telefonnummer konnte nicht gelöscht werden')
    } finally {
      setDeletingId(null)
    }
  }

  const cancelEdit = () => {
    resetForm()
    setIsAdding(false)
    setEditingId(null)
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Phone className="h-5 w-5 text-slate-400" />
          Telefonnummern
        </h2>
        {!isAdding && !editingId && (
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Hinzufügen
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {kontakt.phones.map((phone) =>
          editingId === phone.id ? (
            <PhoneForm
              key={phone.id}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={cancelEdit}
              isSaving={isSaving}
            />
          ) : (
            <div
              key={phone.id}
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 group"
            >
              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-200 text-slate-700">
                  {phone.typ_display}
                </span>
                {phone.is_primary && (
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                )}
                <span className="text-sm text-slate-900">{phone.nummer}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => startEdit(phone)}
                  className="h-8 w-8 p-0"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(phone.id)}
                  disabled={deletingId === phone.id}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  {deletingId === phone.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )
        )}

        {isAdding && (
          <PhoneForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={cancelEdit}
            isSaving={isSaving}
          />
        )}

        {kontakt.phones.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 text-center py-4">
            Keine Telefonnummern vorhanden
          </p>
        )}
      </div>
    </div>
  )
}

function PhoneForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isSaving,
}: {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  isSaving: boolean
}) {
  return (
    <div className="p-4 rounded-lg border border-slate-300 bg-slate-50 space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Typ</Label>
          <Select
            value={formData.typ}
            onValueChange={(value) => setFormData({ ...formData, typ: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PHONE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Nummer</Label>
          <Input
            value={formData.nummer}
            onChange={(e) => setFormData({ ...formData, nummer: e.target.value })}
            placeholder="+49 123 456789"
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={formData.is_primary}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_primary: !!checked })
              }
            />
            <span className="text-sm text-slate-700">Primär</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          <X className="h-4 w-4 mr-1" />
          Abbrechen
        </Button>
        <Button onClick={onSave} disabled={isSaving || !formData.nummer}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          Speichern
        </Button>
      </div>
    </div>
  )
}

// ============ EMAILS SECTION ============

function EmailsSection({ kontakt, onRefresh }: { kontakt: KontaktDetail; onRefresh: () => void }) {
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [formData, setFormData] = useState({
    typ: 'geschaeft' as string,
    email: '',
    is_primary: false,
  })

  const resetForm = () => {
    setFormData({ typ: 'geschaeft', email: '', is_primary: false })
  }

  const startEdit = (emailRecord: KontaktEmail) => {
    setEditingId(emailRecord.id)
    setFormData({
      typ: emailRecord.typ,
      email: emailRecord.email,
      is_primary: emailRecord.is_primary,
    })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (editingId) {
        await kontakteAPI.updateEmail(kontakt.id, editingId, formData)
        toast.success('E-Mail aktualisiert')
      } else {
        await kontakteAPI.createEmail(kontakt.id, formData)
        toast.success('E-Mail hinzugefügt')
      }
      onRefresh()
      resetForm()
      setIsAdding(false)
      setEditingId(null)
    } catch (err) {
      console.error('Failed to save email:', err)
      toast.error('E-Mail konnte nicht gespeichert werden')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (emailId: number) => {
    setDeletingId(emailId)
    try {
      await kontakteAPI.deleteEmail(kontakt.id, emailId)
      toast.success('E-Mail gelöscht')
      onRefresh()
    } catch (err) {
      console.error('Failed to delete email:', err)
      toast.error('E-Mail konnte nicht gelöscht werden')
    } finally {
      setDeletingId(null)
    }
  }

  const cancelEdit = () => {
    resetForm()
    setIsAdding(false)
    setEditingId(null)
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="h-5 w-5 text-slate-400" />
          E-Mail-Adressen
        </h2>
        {!isAdding && !editingId && (
          <Button variant="outline" size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Hinzufügen
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {kontakt.emails.map((emailRecord) =>
          editingId === emailRecord.id ? (
            <EmailForm
              key={emailRecord.id}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSave}
              onCancel={cancelEdit}
              isSaving={isSaving}
            />
          ) : (
            <div
              key={emailRecord.id}
              className="flex items-center justify-between p-3 rounded-lg bg-slate-50 group"
            >
              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-200 text-slate-700">
                  {emailRecord.typ_display}
                </span>
                {emailRecord.is_primary && (
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                )}
                <span className="text-sm text-slate-900">{emailRecord.email}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => startEdit(emailRecord)}
                  className="h-8 w-8 p-0"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(emailRecord.id)}
                  disabled={deletingId === emailRecord.id}
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                >
                  {deletingId === emailRecord.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )
        )}

        {isAdding && (
          <EmailForm
            formData={formData}
            setFormData={setFormData}
            onSave={handleSave}
            onCancel={cancelEdit}
            isSaving={isSaving}
          />
        )}

        {kontakt.emails.length === 0 && !isAdding && (
          <p className="text-sm text-slate-500 text-center py-4">
            Keine E-Mail-Adressen vorhanden
          </p>
        )}
      </div>
    </div>
  )
}

function EmailForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isSaving,
}: {
  formData: any
  setFormData: (data: any) => void
  onSave: () => void
  onCancel: () => void
  isSaving: boolean
}) {
  return (
    <div className="p-4 rounded-lg border border-slate-300 bg-slate-50 space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Typ</Label>
          <Select
            value={formData.typ}
            onValueChange={(value) => setFormData({ ...formData, typ: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMAIL_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>E-Mail</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="name@beispiel.de"
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={formData.is_primary}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_primary: !!checked })
              }
            />
            <span className="text-sm text-slate-700">Primär</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          <X className="h-4 w-4 mr-1" />
          Abbrechen
        </Button>
        <Button onClick={onSave} disabled={isSaving || !formData.email}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          Speichern
        </Button>
      </div>
    </div>
  )
}
